import CloseIcon from '@mui/icons-material/Close';
import { IconButton } from '@mui/material';
import { SyntheticEvent, useCallback } from 'react';
import { useGroupRules, UserGroupRules } from 'src/graphql';
import { GlobalTab, useGlobalTabs } from 'src/utils/hooks';
import { useNavigateRoute } from 'src/utils/routing';
import { useScreenDimensions } from 'src/utils/system';
import { TABS_ENABLED_ON_ROUTES } from './enabled-routes';
import { GlobalTabsBar, GlobalTabsContainer, Tab } from './styled';
import { TabLabel } from './tab-labels';

export const GlobalTabs = () => {
  const { isMobile } = useScreenDimensions();
  const { pathnameWithoutPrefix } = useNavigateRoute();
  const { activeTab, navigateToTab, tabs, closeTab } = useGlobalTabs();
  const { groupRules } = useGroupRules();
  const accessToTabsInProvision = groupRules[UserGroupRules.AccessToTabsInProvision];

  const handleChange = useCallback(
    (_: SyntheticEvent, value: string) => {
      navigateToTab(tabs.find((tab) => tab.id === value)!);
    },
    [navigateToTab, tabs],
  );
  const onClose = useCallback(
    (tab: GlobalTab) => (event: SyntheticEvent) => {
      event.stopPropagation();
      closeTab(tab);
    },
    [closeTab],
  );

  if (!accessToTabsInProvision) return null;
  // Ensure tabs rendered for specific views only.
  if (!TABS_ENABLED_ON_ROUTES.some((route) => pathnameWithoutPrefix.startsWith(route))) {
    return null;
  }
  if (!tabs.length) return null;
  if (isMobile) return null;
  return (
    <GlobalTabsBar data-testid="global-tabs">
      <GlobalTabsContainer
        value={activeTab?.id || false}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
      >
        {tabs.map((tab) => (
          <Tab
            key={tab.id}
            value={tab.id}
            data-testid="global-tab"
            label={
              <>
                <TabLabel {...tab} />
                <IconButton
                  className="close-tab-button"
                  size="small"
                  component="div"
                  onClick={onClose(tab)}
                >
                  <CloseIcon sx={{ fontSize: '0.875rem' }} />
                </IconButton>
              </>
            }
          />
        ))}
      </GlobalTabsContainer>
    </GlobalTabsBar>
  );
};
